import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import './discoverProducts.scss';
import ProductItem from './components/ProductItem';
import { ReactComponent as ProductOrbit } from "./components/ProductOrbit.svg";
import { ReactComponent as ProductOrbitAdaptive } from "./components/ProductOrbitAdaptive.svg";
import { gsap } from "gsap";
import MotionPathPlugin from "gsap/MotionPathPlugin";

gsap.registerPlugin(MotionPathPlugin);

const DiscoverProductsSection = () => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const animationsRef = useRef<any[]>([]);
	
	const [zIndex, setZindex] = useState(1);
	const [activeDescription, setActiveDescription] = useState<string>(""); // Для хранения активного описания

	const stackNote = (ref: any) => {
		ref.current.style.zIndex = zIndex;
		setZindex((zIndex) => zIndex + 1);
	};

	const [width, setWidth]   = useState(window.innerWidth);

	const updateDimensions = () => {
		setWidth(window.innerWidth);
	}
	
	useEffect(() => {
		window.addEventListener("resize", updateDimensions);
		return () => window.removeEventListener("resize", updateDimensions);
	}, []);

	const handleHover = (description: string) => {
		setActiveDescription(description || "");
	};

	const clearDescription = () => {
		setActiveDescription("");
	};
	
	useLayoutEffect(() => {
		const block = containerRef.current;
		
		const startAnimation = () => {
			const products = [
				{ id: "something-product-id", contentId: "something-product-content-id", path: "#product-orbit-item-1", delay: 0 },
				{ id: "soon-product-id-1", contentId: "soon-product-content-id-1", path: "#product-orbit-item-1", delay: -40 },
				{ id: "soon-product-id-2", contentId: "soon-product-content-id-2", path: "#product-orbit-item-1", delay: -80 },
				{ id: "trading-product-id", contentId: "trading-product-content-id", path: "#product-orbit-item-1", delay: -120 },
				{ id: "soon-product-id-3", contentId: "soon-product-content-id-3", path: "#product-orbit-item-1", delay: -160 },
				{ id: "combine-product-id", contentId: "combine-product-content-id", path: "#product-orbit-item-2", delay: 0, from: true },
				{ id: "armor-product-id", contentId: "armor-product-content-id", path: "#product-orbit-item-2", delay: -130, from: true },
				{ id: "platform-product-id", contentId: "platform-product-content-id", path: "#product-orbit-item-2", delay: -260, from: true }
			];
			
			products.forEach(({ id, contentId, path, delay, from }) => {
				const product = document.getElementById(id);
				const productContent = document.getElementById(contentId);
				
				if (product && productContent) {
					const animation = (from ? gsap.from : gsap.to)(product, {
						duration: 200,
						motionPath: {
							path: path,
							align: path,
							alignOrigin: [0.5, 0.5],
							autoRotate: true,
						},
						delay: delay,
						repeat: -1,
						ease: 'none',
						onUpdate: () => {
							const angle = gsap.getProperty(product, "rotation");
							gsap.set(productContent, { rotation: -angle });
						},
					});
					animationsRef.current.push(animation);
				} else {
					console.warn(`Element #${id} not found`);
				}
			});
		};
		
		const stopAnimation = () => {
			animationsRef.current.forEach((animation) => animation.kill());
			animationsRef.current = [];
		};
		
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					startAnimation();
				} else {
					stopAnimation();
				}
			},
			{ threshold: 0.1 }
		);
		
		if (block) observer.observe(block);
		
		return () => {
			observer.disconnect();
			stopAnimation();
		};
		
	}, []);

	return (
		<div className="discoverProducts" ref={containerRef}>
			<div className="stars"></div>
			<div className="stars2"></div>
			<div className="stars3"></div>
			<div className="discoverProducts__info">
				<h2>One ecosystem for multiple needs</h2>
				<div className="discoverProducts__info_description">
					<span>Discover all our products and features that will help you with various tasks when interacting with the Web-3 world.</span>
				</div>
			</div>
			<div className="discoverProducts__content">
				{
					width > 425 ? (
						<ProductOrbit className={"orbit-11"}/>
					) : (
						<ProductOrbitAdaptive className={"orbit-11"}/>
					)
				}
				<ProductItem
					id={'something-product-id'}
					contentId={'something-product-content-id'}
					stackNote={stackNote}
					title={"Something Cool"}
					description={"Launch, trade, earn with liquidity provider and build community right inside the platform."}
					type={"Something"}
					isAvailable={true}
					onHover={handleHover}
					onLeave={clearDescription}
				/>
				<ProductItem
					id={'soon-product-id-1'}
					contentId={'soon-product-content-id-1'}
					stackNote={stackNote}
					title={"Soon"}
					description={""}
					type={"Soon"}
					isAvailable={false}
					onHover={handleHover}
					onLeave={clearDescription}
				/>
				<ProductItem
					id={'soon-product-id-2'}
					contentId={'soon-product-content-id-2'}
					stackNote={stackNote}
					title={"Soon"}
					description={""}
					type={"Soon"}
					isAvailable={false}
					onHover={handleHover}
					onLeave={clearDescription}
				/>
				<ProductItem
					id={'trading-product-id'}
					contentId={'trading-product-content-id'}
					stackNote={stackNote}
					title={"Trading Bots"}
					description={"Trading bots inside Telegram. Gain instant access to trading various tokens across different networks."}
					type={"Trading"}
					isAvailable={true}
					onHover={handleHover}
					onLeave={clearDescription}
				/>
				<ProductItem
					id={'soon-product-id-3'}
					contentId={'soon-product-content-id-3'}
					stackNote={stackNote}
					title={"Soon"}
					description={""}
					type={"Soon"}
					isAvailable={false}
					onHover={handleHover}
					onLeave={clearDescription}
				/>
				<ProductItem
					id={'combine-product-id'}
					contentId={'combine-product-content-id'}
					stackNote={stackNote}
					title={"Combine"}
					description={"Manage your cryptocurrency wallets and farms, automate interactions with Web-3 projects and earn retros easily within a single application."}
					type={"Combine"}
					isAvailable={true}
					onHover={handleHover}
					onLeave={clearDescription}
				/>
				<ProductItem
					id={'armor-product-id'}
					contentId={'armor-product-content-id'}
					stackNote={stackNote}
					title={"Armor"}
					description={"Complete control over your team's work with absolute security for your assets."}
					type={"Armor"}
					isAvailable={true}
					onHover={handleHover}
					onLeave={clearDescription}
				/>
				<ProductItem
					id={'platform-product-id'}
					contentId={'platform-product-content-id'}
					stackNote={stackNote}
					title={"Platform"}
					description={"All Web-3 project activities worth your attention in one place."}
					type={"Platform"}
					isAvailable={true}
					onHover={handleHover}
					onLeave={clearDescription}
				/>
			</div>
			{activeDescription && (
				<div className="discoverProducts__description">
					<span>{activeDescription}</span>
				</div>
			)}
			<div className="discoverProducts__background"></div>
		</div>
	);
};

export default DiscoverProductsSection;